import React from 'react';
import { type LucideIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type StatTileTone = 'brand' | 'success' | 'warning' | 'destructive' | 'info' | 'neutral';

const toneChip: Record<StatTileTone, string> = {
    brand: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
    info: 'bg-info/10 text-info',
    neutral: 'bg-muted text-muted-foreground',
};

const toneValue: Record<StatTileTone, string> = {
    brand: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
    info: 'text-info',
    neutral: 'text-foreground',
};

export interface StatTileProps {
    label: string;
    value: React.ReactNode;
    icon?: LucideIcon;
    /** Tints the icon chip, and the value when `emphasizeValue` is set. */
    tone?: StatTileTone;
    /** Colour the number too. Use sparingly: money in/out, failures. */
    emphasizeValue?: boolean;
    /** Secondary line under the value. */
    hint?: React.ReactNode;
    /** Signed percentage/absolute change, rendered with a trend arrow. */
    delta?: { value: string; direction: 'up' | 'down' | 'flat' };
    isLoading?: boolean;
    onClick?: () => void;
    className?: string;
}

/**
 * The single KPI tile for the CRM.
 *
 * Glass is deliberately kept here: stat tiles are the one "hero" surface in the
 * content area, while tables, forms and detail cards use the flat `Card`.
 */
export const StatTile: React.FC<StatTileProps> = ({
    label,
    value,
    icon: Icon,
    tone = 'brand',
    emphasizeValue = false,
    hint,
    delta,
    isLoading = false,
    onClick,
    className,
}) => {
    const interactive = typeof onClick === 'function';
    const Comp = interactive ? 'button' : 'div';

    return (
        <Comp
            {...(interactive ? { type: 'button' as const, onClick } : {})}
            className={cn(
                'glass-card relative flex items-start justify-between gap-3 rounded-xl p-4 text-left',
                interactive &&
                    'cursor-pointer transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                className
            )}
        >
            <div className="min-w-0 flex-1">
                {/* Wraps rather than truncates: "Total Transactions" is unreadable
                    as "Total …", and tiles are often four-or-more across. */}
                <p className="text-xs font-medium uppercase leading-tight tracking-wide text-muted-foreground">
                    {label}
                </p>

                {isLoading ? (
                    <Skeleton className="mt-2 h-7 w-20" />
                ) : (
                    <p
                        className={cn(
                            'mt-1 truncate text-2xl font-semibold tabular-figures',
                            emphasizeValue ? toneValue[tone] : 'text-foreground'
                        )}
                    >
                        {value}
                    </p>
                )}

                {(hint || delta) && !isLoading && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        {delta && (
                            <span
                                className={cn(
                                    'inline-flex items-center gap-0.5 font-medium',
                                    delta.direction === 'up' && 'text-success',
                                    delta.direction === 'down' && 'text-destructive'
                                )}
                            >
                                {delta.direction === 'up' && <TrendingUp className="h-3 w-3" />}
                                {delta.direction === 'down' && <TrendingDown className="h-3 w-3" />}
                                {delta.value}
                            </span>
                        )}
                        {hint && <span className="truncate">{hint}</span>}
                    </div>
                )}
            </div>

            {Icon && (
                <span
                    aria-hidden="true"
                    className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                        toneChip[tone]
                    )}
                >
                    <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
            )}
        </Comp>
    );
};

/** Responsive grid for a row of StatTiles. Never a fixed column count. */
export const StatTileGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className,
}) => (
    <div
        className={cn(
            'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
            className
        )}
    >
        {children}
    </div>
);

export default StatTile;
